import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';
import type { HierarchyPageVM } from './useHierarchyPage';

export interface OrgDeactivateDialogProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: frame 32's first half — `Ngưng hoạt động "X"?`.
 *
 * WIRED TO `DELETE /organizations/{id}`, which is a SOFT deactivate: portal-api
 * flips `is_active` and never deletes the row, because Level, OrganizationAdmin
 * and Assignment rows all FK the organization. That is why every consequence
 * below is reversible and why the fourth bullet can promise it.
 *
 * THE ACCOUNT FIGURE, honestly sourced. The mock shows a static "124". The
 * authoritative count (`accounts_affected`, distinct Assignment +
 * OrganizationAdmin subjects) exists only in the DELETE *response*, so it cannot
 * be shown before the action. This dialog therefore states the org's active
 * assignment count from `provisioning-status` — a real pre-flight number, and
 * labelled as that rather than as the affected total — and the success toast
 * reports the exact `accounts_affected` the server returned.
 *
 * Super-admin only: rendered behind `canManageTree` (= `console.orgs.manage`,
 * issued to super-admins alone), matching the server's own gate. The note about
 * 404-vs-403 is the design's, and it is accurate — an org the caller cannot
 * administer answers NOT_FOUND so ids do not leak, while their OWN org answers
 * AUTH_PERMISSION_DENIED.
 */
export function OrgDeactivateDialog({ vm }: OrgDeactivateDialogProps) {
    const { t } = useTranslation();
    const {
        confirmDeactivateNode,
        setConfirmDeactivateOrgId,
        deactivatePreflightQuery,
        deactivateOrg,
    } = vm;

    const activeAccounts = deactivatePreflightQuery.data?.assignments.active ?? null;
    const name = confirmDeactivateNode?.organization.name ?? '';

    const accountsBullet =
        activeAccounts === null
            ? t('console.hierarchy.deactivateDialog.accountsBulletUnknown')
            : t('console.hierarchy.deactivateDialog.accountsBulletKnown', {
                  value: activeAccounts.toLocaleString('vi-VN'),
              });

    return (
        <Modal
            open={Boolean(confirmDeactivateNode)}
            onCancel={() => setConfirmDeactivateOrgId(null)}
            title={
                confirmDeactivateNode
                    ? t('console.hierarchy.deactivateDialog.title', { name })
                    : ''
            }
            footer={
                <div className="hz-move-footer">
                    <span className="hz-move-footer-note">
                        {t('console.hierarchy.auditLogNote')}
                    </span>
                    <span className="hz-move-footer-actions">
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={() => setConfirmDeactivateOrgId(null)}
                            disabled={deactivateOrg.isPending}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-danger"
                            disabled={deactivateOrg.isPending || !confirmDeactivateNode}
                            onClick={() => {
                                if (confirmDeactivateNode)
                                    deactivateOrg.mutate(confirmDeactivateNode.id);
                            }}
                        >
                            {deactivateOrg.isPending
                                ? t('console.hierarchy.processingLabel')
                                : t('console.hierarchy.deactivateDialog.confirmButton')}
                        </button>
                    </span>
                </div>
            }
        >
            <p className="hz-card-note">{t('console.hierarchy.deactivateDialog.description')}</p>

            <ul className="hz-consequences">
                <li className="hz-consequence hz-consequence--warn">{accountsBullet}</li>
                <li className="hz-consequence hz-consequence--warn">
                    {t('console.hierarchy.deactivateDialog.consequenceNoNewAccounts')}
                </li>
                <li className="hz-consequence hz-consequence--warn">
                    {t('console.hierarchy.deactivateDialog.consequenceNoIncomingMoves')}
                </li>
                <li className="hz-consequence hz-consequence--ok">
                    {t('console.hierarchy.deactivateDialog.consequenceReversible')}
                </li>
            </ul>

            <p className="hz-card-note hz-deactivate-note">
                {t('console.hierarchy.deactivateDialog.orgAdminNote')}
            </p>
        </Modal>
    );
}

export default OrgDeactivateDialog;
